'use client';

import type { CSSProperties, ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import Link from 'next/link';
import { CitableStat, type CitableStatProps } from '@/components/seo/citable-stat';
import {
  trackAeoCitableStatView,
  trackAeoFaqExpand,
  trackAeoHubLinkClick,
  trackAeoPageView,
  trackAeoPrimaryCtaClick,
  type AeoCtaLocation,
  type AeoPageFamily,
  type AeoTargetTerm,
} from '@/lib/analytics/enhanced-events';

export function AeoPageViewTracker({
  pageFamily,
  targetTerm,
}: {
  pageFamily: AeoPageFamily;
  targetTerm: AeoTargetTerm;
}) {
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    trackAeoPageView({ page_family: pageFamily, target_term: targetTerm });
  }, [pageFamily, targetTerm]);

  return null;
}

export function AeoPrimaryCtaLink({
  href,
  pageFamily,
  ctaLocation,
  children,
  className,
  style,
}: {
  href: string;
  pageFamily: AeoPageFamily;
  ctaLocation: AeoCtaLocation;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <Link
      href={href}
      className={className}
      style={style}
      onClick={() =>
        trackAeoPrimaryCtaClick({
          page_family: pageFamily,
          cta_location: ctaLocation,
        })}
    >
      {children}
    </Link>
  );
}

export function AeoHubLink({
  href,
  sourcePage,
  anchorText,
  children,
  className,
  style,
}: {
  href: string;
  sourcePage: string;
  anchorText: string;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <Link
      href={href}
      className={className}
      style={style}
      onClick={() =>
        trackAeoHubLinkClick({
          source_page: sourcePage,
          dest_page: href,
          anchor_text: anchorText,
        })}
    >
      {children}
    </Link>
  );
}

export function AeoTrackedCitableStat({
  pageFamily,
  claim,
  ...props
}: CitableStatProps & {
  pageFamily: AeoPageFamily;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const firedRef = useRef(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || firedRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const isVisible = entries.some((entry) => entry.isIntersecting);
        if (!isVisible || firedRef.current) return;
        firedRef.current = true;
        trackAeoCitableStatView({ claim, page_family: pageFamily });
        observer.disconnect();
      },
      { threshold: 0.4 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [claim, pageFamily]);

  return (
    <div ref={ref}>
      <CitableStat claim={claim} {...props} />
    </div>
  );
}

export function AeoTrackedFaqItem({
  question,
  answer,
  position,
  pageFamily,
}: {
  question: string;
  answer: string;
  position: number;
  pageFamily: AeoPageFamily;
}) {
  return (
    <details
      className="v4-faq"
      onToggle={(event) => {
        if (!(event.currentTarget as HTMLDetailsElement).open) return;
        trackAeoFaqExpand({
          question,
          position,
          page_family: pageFamily,
        });
      }}
    >
      <summary>{question}</summary>
      <div className="v4-faq__answer speakable-answer">{answer}</div>
    </details>
  );
}
